import React, { useEffect, useState } from 'react';
import { Headphones, Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { openSupportChat } from '../lib/supportBus';
import SupportChat from './SupportChat';
import TrackInvoiceAccess from './TrackInvoiceAccess';

const NAV_ITEMS = [
  { label: 'Services', path: '/services' },
  { label: 'How it works', path: '/how-it-works' },
  { label: 'What is Giggs?', path: '/what-is-giggs' },
  { label: 'Track a Gig', path: '/track' },
];

const accountMeta = (user) => {
  if (user?.role === 'provider') return { label: 'Gigger dashboard', path: '/provider/dashboard' };
  if (user?.role === 'admin') return { label: 'Admin dashboard', path: '/admin/dashboard' };
  return { label: 'Client portal', path: '/client/dashboard' };
};

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const account = accountMeta(user);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const navClass = (path) =>
    `relative text-sm font-semibold no-underline transition-all duration-300 after:absolute after:-bottom-2 after:left-0 after:h-0.5 after:rounded-full after:bg-green-600 after:transition-all after:duration-300 ${
      location.pathname === path
        ? 'text-green-700 after:w-full'
        : 'text-slate-700 after:w-0 hover:text-green-700 hover:after:w-full'
    }`;

  const buttonMotion =
    'transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:scale-[0.99]';

  return (
    <>
      <header
        className={`sticky top-0 z-50 border-b transition-all duration-300 ${
          scrolled
            ? 'border-slate-200/80 bg-white/95 shadow-[0_10px_30px_rgba(15,23,42,0.07)] backdrop-blur-xl'
            : 'border-transparent bg-white/95 backdrop-blur'
        }`}
      >
        <div className="mx-auto flex min-h-[68px] items-center justify-between gap-4 px-4 py-3 sm:min-h-[72px] sm:px-6 lg:gap-6 lg:px-8 2xl:px-10">
          <Link to="/" className="flex shrink-0 items-center no-underline transition-transform duration-300 hover:scale-105" aria-label="Giggs home">
            <img src="/images/logo.webp" alt="Giggs" className="h-9 w-auto object-contain sm:h-10" />
          </Link>

          <nav className="hidden min-w-0 items-center gap-5 lg:flex xl:gap-8" aria-label="Main navigation">
            {NAV_ITEMS.map((item) => (
              <Link key={item.path} to={item.path} className={navClass(item.path)}>
                {item.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={openSupportChat}
              className="inline-flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-sm font-semibold text-slate-700 transition-all duration-300 hover:-translate-y-0.5 hover:text-green-700"
            >
              <Headphones size={17} />
              Help
            </button>
          </nav>

          <div className="hidden shrink-0 items-center gap-2 md:flex">
            {isAuthenticated ? (
              <>
                <Link to={account.path} className={`rounded-xl border border-green-700 bg-white px-3 py-2.5 text-sm font-bold text-green-700 no-underline hover:bg-green-50 xl:px-4 ${buttonMotion}`}>
                  {account.label}
                </Link>
                {user?.role === 'client' && (
                  <Link to="/book" className={`rounded-xl bg-green-700 px-3 py-2.5 text-sm font-bold text-white no-underline shadow-[0_7px_18px_rgba(21,128,61,0.18)] hover:bg-green-800 xl:px-4 ${buttonMotion}`}>
                    Book a service
                  </Link>
                )}
              </>
            ) : (
              <>
                <Link to="/login" className={`rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 no-underline hover:bg-slate-100 xl:px-4 ${buttonMotion}`}>
                  Log in
                </Link>
                <Link to="/provider-register" className={`rounded-xl border border-green-700 bg-white px-3 py-2.5 text-sm font-bold text-green-700 no-underline hover:bg-green-50 xl:px-4 ${buttonMotion}`}>
                  Become a Gigger
                </Link>
                <Link to="/book" className={`rounded-xl bg-green-700 px-3 py-2.5 text-sm font-bold text-white no-underline shadow-[0_7px_18px_rgba(21,128,61,0.18)] hover:bg-green-800 xl:px-4 ${buttonMotion}`}>
                  Book a service
                </Link>
              </>
            )}
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-800 shadow-sm transition-all duration-300 hover:border-green-200 hover:bg-green-50 hover:text-green-700 hover:shadow-md md:hidden sm:h-11 sm:w-11"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-slate-100 bg-white shadow-lg md:hidden">
            <nav className="mx-auto flex w-full max-w-[1920px] flex-col gap-1 px-4 pb-6 pt-3 sm:px-6" aria-label="Mobile navigation">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="rounded-xl px-3 py-3 text-base font-semibold text-slate-800 no-underline transition-all duration-200 hover:translate-x-1 hover:bg-green-50 hover:text-green-700"
                >
                  {item.label}
                </Link>
              ))}
              <button
                type="button"
                onClick={() => {
                  openSupportChat();
                  setMenuOpen(false);
                }}
                className="flex cursor-pointer items-center gap-2 rounded-xl border-0 bg-transparent px-3 py-3 text-left text-base font-semibold text-slate-800 transition-all duration-200 hover:translate-x-1 hover:bg-green-50 hover:text-green-700"
              >
                <Headphones size={18} /> Help
              </button>

              {isAuthenticated ? (
                <div className="mt-3 grid gap-2">
                  <Link to={account.path} className={`rounded-xl border border-green-700 px-4 py-3 text-center text-sm font-bold text-green-700 no-underline ${buttonMotion}`}>
                    {account.label}
                  </Link>
                  {user?.role === 'client' && (
                    <Link to="/book" className={`rounded-xl bg-green-700 px-4 py-3 text-center text-sm font-bold text-white no-underline ${buttonMotion}`}>
                      Book a service
                    </Link>
                  )}
                </div>
              ) : (
                <>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    <Link to="/client-register" className={`rounded-xl border border-slate-200 px-4 py-3 text-center text-sm font-bold text-slate-800 no-underline ${buttonMotion}`}>
                      Create client account
                    </Link>
                    <Link to="/provider-register" className={`rounded-xl border border-green-700 px-4 py-3 text-center text-sm font-bold text-green-700 no-underline ${buttonMotion}`}>
                      Become a Gigger
                    </Link>
                  </div>
                  <Link to="/book" className={`mt-2 rounded-xl bg-green-700 px-4 py-3 text-center text-sm font-bold text-white no-underline ${buttonMotion}`}>
                    Book a service
                  </Link>
                  <Link to="/login" className="px-4 py-2 text-center text-sm font-semibold text-slate-600 no-underline transition-colors hover:text-green-700">
                    Already have an account? Log in
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </header>

      <TrackInvoiceAccess />
      <SupportChat />
    </>
  );
};

export default Header;
